import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";

import {handleServersideValidationErrors} from "utils/forms";

import validation from "./validation";
import ImageField from "components/Forms/ImageField";
import HiddenField from "components/Forms/HiddenField";
import Spinner from "components/Spinner";
import NumberField from "components/Forms/NumberField";
import SelectPartidoField from "components/Partidos/SelectPartidoField";
import LocationSelector from "./LocationSelector";
import PartidosTable from "./PartidosTable";
import VotosEspeciales from "./VotosEspeciales";

const ActaForm = ({
  onSubmit,
  acta,
  isReadonly,
  isSubmitting,
  errors,
  onCancel,
  actasCargadas = [],
  onFormReset
}) => {
  const [totalVotos, setTotalVotos] = useState(0);
  const [totalConcejales, setTotalConcejales] = useState(0);
  const [totalLegisladores, setTotalLegisladores] = useState(0);
  const [selectedPartido, setSelectedPartido] = useState(null);
  const [partidosAgregados, setPartidosAgregados] = useState([]);
  const [partidos, setPartidos] = useState([]);

  // Estado local para votos especiales
  const [votosEspeciales, setVotosEspeciales] = useState({
    blancos: { concejales: 0, legisladores_provinciales: 0 },
    impugnados: { concejales: 0, legisladores_provinciales: 0 }
  });

  // Estado local para votos de partidos
  const [votosPartidos, setVotosPartidos] = useState({});

  // Estado para los datos actuales del PartidosTable
  const [partidosTableData, setPartidosTableData] = useState([]);

  // Estados locales para location (manejados manualmente)
  const [localidadSeleccionada, setLocalidadSeleccionada] = useState(null);
  const [escuelaSeleccionada, setEscuelaSeleccionada] = useState(null);
  const [mesaSeleccionada, setMesaSeleccionada] = useState(null);

  // Estado para controlar el reset del LocationSelector
  const [resetLocationSelector, setResetLocationSelector] = useState(false);

  // Key para forzar el re-render del PartidosTable
  const [partidosTableKey, setPartidosTableKey] = useState(0);

  const form = useForm({
    resolver: joiResolver(validation),
    defaultValues: {
      especiales: {
        blancos: { concejales: 0, legisladores_provinciales: 0 },
        impugnados: { concejales: 0, legisladores_provinciales: 0 }
      },
      electores_votaron: 0,
      sobres_urna: 0,
      diferencia_votos: 0,
      votos_partidos: {},
      detalle: [],
      ...acta
    },
    mode: "all",
  });

  // Observar cambios en los campos específicos para calcular el total
  const watchEspeciales = useWatch({
    control: form.control,
    name: 'especiales',
    defaultValue: {}
  });

  const watchDetalle = useWatch({
    control: form.control,
    name: 'detalle'
  });

  const watchElectoresVotaron = useWatch({
    control: form.control,
    name: 'electores_votaron'
  });

  const watchSobresUrna = useWatch({
    control: form.control,
    name: 'sobres_urna'
  });

  const watchVotosPartidos = useWatch({
    control: form.control,
    name: 'votos_partidos'
  });

  // Calcular diferencia entre electores que votaron y sobres en urna
  useEffect(() => {
    const electoresVotaron = parseInt(watchElectoresVotaron) || 0;
    const sobresUrna = parseInt(watchSobresUrna) || 0;
    const diferencia = electoresVotaron - sobresUrna;

    // Actualizar el campo diferencia_votos
    form.setValue('diferencia_votos', diferencia);

  }, [watchElectoresVotaron, watchSobresUrna, form]);

  // Callbacks para manejar cambios en location
  const handleLocalidadChange = (localidadId) => {

    setLocalidadSeleccionada(localidadId);
  };

  const handleEscuelaChange = (escuelaId) => {

    setEscuelaSeleccionada(escuelaId);
  };

  const handleMesaChange = (mesaId) => {

    setMesaSeleccionada(mesaId);
  };

  // Calcular total de votos emitidos
  useEffect(() => {
    let total = 0;
    let totalConcejales = 0;
    let totalLegisladores = 0;

    // Sumar votos especiales - usar watchEspeciales si está disponible, sino votosEspeciales
    const especiales = watchEspeciales && Object.keys(watchEspeciales).length > 0 ? watchEspeciales : votosEspeciales;

    if (especiales.blancos) {
      if (typeof especiales.blancos === 'object') {
        const concejales = parseInt(especiales.blancos.concejales) || 0;
        const legisladores = parseInt(especiales.blancos.legisladores_provinciales) || 0;
        totalConcejales += concejales;
        totalLegisladores += legisladores;
        total += concejales + legisladores;
      } else {
        const votos = parseInt(especiales.blancos) || 0;
        totalLegisladores += votos; // Formato antiguo va a legisladores
        total += votos;
      }
    }

    if (especiales.impugnados) {
      if (typeof especiales.impugnados === 'object') {
        const concejales = parseInt(especiales.impugnados.concejales) || 0;
        const legisladores = parseInt(especiales.impugnados.legisladores_provinciales) || 0;
        totalConcejales += concejales;
        totalLegisladores += legisladores;
        total += concejales + legisladores;
      } else {
        const votos = parseInt(especiales.impugnados) || 0;
        totalLegisladores += votos; // Formato antiguo va a legisladores
        total += votos;
      }
    }



    // Obtener otros valores del formulario - usar watchDetalle si está disponible (modo editar), sino form.getValues
    const detalle = watchDetalle && Array.isArray(watchDetalle) && watchDetalle.length > 0 ? watchDetalle : (form.getValues('detalle') || []);
    const electoresVotaron = form.getValues('electores_votaron') || 0;

    console.log('🔍 DEBUG - watchDetalle:', watchDetalle);
    console.log('🔍 DEBUG - detalle final usado:', detalle);

    // Sumar votos de las listas
    if (Array.isArray(detalle)) {
      detalle.forEach(lista => {
        if (lista && typeof lista === 'object') {
          if (lista.presidente) {
            const votos = parseInt(lista.presidente) || 0;
            totalLegisladores += votos;
            total += votos;
          }
          if (lista.diputados_nacionales) {
            const votos = parseInt(lista.diputados_nacionales) || 0;
            totalLegisladores += votos;
            total += votos;
          }
          if (lista.legisladores_provinciales) {
            const votos = parseInt(lista.legisladores_provinciales) || 0;
            totalLegisladores += votos;
            total += votos;
          }
          if (lista.concejales) {
            const votos = parseInt(lista.concejales) || 0;
            totalConcejales += votos;
            total += votos;
          }
        }
      });
    }

    // Sumar votos de partidos agregados dinámicamente
    partidosAgregados.forEach(partido => {
      const votos = parseInt(partido.votos) || 0;
      totalLegisladores += votos; // Por defecto van a legisladores
      total += votos;
    });

    // Sumar votos de la tabla de partidos desde el estado local (igual que votos especiales)
    if (votosPartidos && typeof votosPartidos === 'object') {
      Object.values(votosPartidos).forEach(partido => {
        if (partido && typeof partido === 'object') {
          if (partido.diputados_senadores) {
            const votos = parseInt(partido.diputados_senadores) || 0;
            totalLegisladores += votos;
            total += votos;
          }
          if (partido.concejales) {
            const votos = parseInt(partido.concejales) || 0;
            totalConcejales += votos;
            total += votos;
          }
        }
      });
      console.log('🔍 DEBUG - Votos de partidos sumados desde estado local:', votosPartidos);
    }

    // NOTA: electoresVotaron NO se suma al total - es solo informativo

    console.log('🔍 DEBUG - Totales calculados:', {
      totalConcejales,
      totalLegisladores,
      total,
      especiales,
      detalle,
      votosPartidos
    });

    setTotalVotos(total);
    setTotalConcejales(totalConcejales);
    setTotalLegisladores(totalLegisladores);

    // NO actualizar total_votantes_padron aquí, se hace en el otro useEffect
  }, [votosEspeciales, votosPartidos, partidosAgregados, form, watchEspeciales, watchDetalle]);

  useEffect(() => {
    handleServersideValidationErrors(errors, form.setError);
  }, [errors, form.setError]);

  const hasErrors = Object.keys(form.errors).length !== 0;

  const handleReset = () => {
    console.log('🔄 DEBUG - handleReset ejecutándose');
    console.log('🔄 DEBUG - acta actual:', acta);

    // Procesar el detalle para separar votos especiales de votos de partidos
    const detallePartidos = [];
    const votosEspeciales = {
      blancos: { concejales: 0, legisladores_provinciales: 0 },
      impugnados: { concejales: 0, legisladores_provinciales: 0 }
    };

    // Los votos especiales ahora vienen en acta.especiales (formato correcto)
    if (acta?.especiales) {
      if (acta.especiales.blancos) {
        votosEspeciales.blancos = {
          concejales: acta.especiales.blancos.concejales || 0,
          legisladores_provinciales: acta.especiales.blancos.legisladores_provinciales || 0
        };
      }
      if (acta.especiales.impugnados) {
        votosEspeciales.impugnados = {
          concejales: acta.especiales.impugnados.concejales || 0,
          legisladores_provinciales: acta.especiales.impugnados.legisladores_provinciales || 0
        };
      }
    }

    // Los votos de partidos vienen en acta.detalle
    if (acta?.detalle && Array.isArray(acta.detalle)) {
      acta.detalle.forEach(item => {
        // Solo agregar si no es un voto especial (por compatibilidad)
        if (item.tipo !== 'blancos' && item.tipo !== 'impugnados') {
          detallePartidos.push(item);
        }
      });
    }

    console.log('🔍 DEBUG - Votos especiales procesados:', votosEspeciales);
    console.log('🔍 DEBUG - Detalle partidos procesado:', detallePartidos);

    // Mapear los campos de la base de datos a los campos del formulario
    const actaMapeada = {
      ...acta,
      // Mapear campos de la base de datos a los nombres del formulario
      electores_votaron: acta?.total_votantes_padron || 0,
      sobres_urna: acta?.sobres || 0,
      diferencia_votos: (acta?.total_votantes_padron || 0) - (acta?.sobres || 0),
      // Mapear votos especiales procesados
      especiales: votosEspeciales,
      // Mapear detalle solo con partidos
      detalle: detallePartidos
    };

    form.reset(actaMapeada);

    // IMPORTANTE: También resetear todos los estados locales
    setVotosEspeciales(votosEspeciales);

    // Resetear votos de partidos (se cargarán desde initialData en PartidosTable)
    setVotosPartidos({});

    // Inicializar datos del PartidosTable con el detalle del acta
    setPartidosTableData(detallePartidos);

    // Resetear ubicación
    setLocalidadSeleccionada(acta?.localidad || acta?.distrito || null);
    setEscuelaSeleccionada(acta?.escuela || null);
    setMesaSeleccionada(acta?.mesa || null);

    // Resetear total de votos
    setTotalVotos(0);

    // Activar reset del LocationSelector
    console.log('🔄 DEBUG - Activando forceReset del LocationSelector');
    setResetLocationSelector(true);

    // Forzar re-render del PartidosTable
    setPartidosTableKey(prev => prev + 1);
  };

  const handleClearAll = () => {

    // Limpiar el formulario completamente
    form.reset({
      electores_votaron: 0,
      sobres_urna: 0,
      diferencia_votos: 0,
      nulos: 0,
      blancos: 0,
      recurridos: 0,
      impugnados: 0,
      votos_partidos: {},
      especiales: {
        blancos: { concejales: 0, legisladores_provinciales: 0 },
        impugnados: { concejales: 0, legisladores_provinciales: 0 }
      }
    });

    // Limpiar estados locales
    setTotalVotos(0);
    setTotalConcejales(0);
    setTotalLegisladores(0);
    setPartidosAgregados([]);
    setVotosPartidos({}); // Limpiar votos de partidos para disparar recálculo
    setVotosEspeciales({
      blancos: { concejales: 0, legisladores_provinciales: 0 },
      impugnados: { concejales: 0, legisladores_provinciales: 0 }
    });

    // Forzar re-render del PartidosTable
    setPartidosTableKey(prev => prev + 1);
  };

  const agregarPartido = () => {
    if (selectedPartido && !partidosAgregados.find(p => p.nombre === selectedPartido.label)) {
      const nuevoPartido = {
        id: selectedPartido.value,
        nombre: selectedPartido.label,
        votos: 0
      };
      setPartidosAgregados([...partidosAgregados, nuevoPartido]);
      setSelectedPartido(null);
    }
  };

  const eliminarPartido = (partidoId) => {
    setPartidosAgregados(partidosAgregados.filter(p => p.id !== partidoId));
  };

  const actualizarVotosPartido = (partidoId, valor) => {
    setPartidosAgregados(partidosAgregados.map(p => 
      p.id === partidoId ? { ...p, votos: valor } : p
    ));
  };

  const handlePartidosChange = (partidosRecibidos) => {
    setPartidos(partidosRecibidos);
    console.log('🎯 Partidos recibidos en ActaForm:', partidosRecibidos);
  };

  // Función para actualizar votos especiales y forzar re-render
  const handleEspecialesChange = (tipo, cargo, valor) => {
    const nuevoValor = Math.max(0, parseInt(valor) || 0);

    // Actualizar el estado local (esto dispara el useEffect del total)
    setVotosEspeciales(prev => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        [cargo]: nuevoValor
      }
    }));

    // También actualizar el formulario para mantener sincronización
    const especialesActuales = form.getValues('especiales') || {};
    form.setValue('especiales', {
      ...especialesActuales,
      [tipo]: {
        ...especialesActuales[tipo],
        [cargo]: nuevoValor
      }
    });


  };

  // Función para actualizar votos de partidos
  const handlePartidosVotosChange = (partidoId, campo, valor) => {
    const nuevoValor = Math.max(0, parseInt(valor) || 0);

    // Actualizar el estado local (esto dispara el useEffect del total)
    setVotosPartidos(prev => ({
      ...prev,
      [partidoId]: {
        ...prev[partidoId],
        [campo]: nuevoValor
      }
    }));

    // También actualizar el formulario para mantener sincronización
    const votosPartidosActuales = form.getValues('votos_partidos') || {};
    form.setValue('votos_partidos', {
      ...votosPartidosActuales,
      [partidoId]: {
        ...votosPartidosActuales[partidoId],
        [campo]: nuevoValor
      }
    });

    console.log(`🎯 Voto de partido ${partidoId} ${campo} actualizado:`, nuevoValor);
  };

  // Función para recibir datos actualizados del PartidosTable
  const handlePartidosTableDataChange = (datosActualizados) => {
    setPartidosTableData(datosActualizados);
    console.log(`🎯 ActaForm - Datos del PartidosTable actualizados:`, datosActualizados);
  };

  const handleSubmit = async (data) => {
    // ✅ Validar que tiene imagen antes de enviar
    if (!data.foto || data.foto.length === 0) {
      console.log('❌ Error: No se ha seleccionado ninguna imagen');
      // Mostrar error al usuario
      form.setError('foto', {
        type: 'manual',
        message: 'Debe seleccionar al menos una imagen del acta'
      });
      return; // No continuar con el envío
    }

    console.log('✅ Validación de imagen exitosa, procediendo con el envío');

    // Crear objeto manual con todos los datos
    const datosManuales = {
      // Datos básicos del formulario
      electores_votaron: data.electores_votaron || 0,
      sobres_urna: data.sobres_urna || 0,
      diferencia_votos: data.diferencia_votos || 0,

      // Location data (desde estados locales)
      distrito: localidadSeleccionada,
      escuela: escuelaSeleccionada,
      mesa: mesaSeleccionada,

      // Votos especiales (desde estado local)
      especiales: {
        blancos: votosEspeciales.blancos || { concejales: 0, legisladores_provinciales: 0 },
        impugnados: votosEspeciales.impugnados || { concejales: 0, legisladores_provinciales: 0 }
      },

      // Detalle existente del acta
      detalle: data.detalle || [],

      // Archivos
      foto: data.foto,
      foto2: data.foto2
    };

    // Transformar los partidos agregados dinámicamente al formato de la base de datos
    const partidosParaGuardar = partidosAgregados.map(partido => ({
      tipo: 'LISTA',
      lista: partido.nombre,
      cargo: 'LEGISLADORES_PROVINCIALES',
      votos: partido.votos
    }));

    // Transformar los votos de la tabla de partidos al formato de acta_detalle
    const partidosTablaParaGuardar = [];
    if (votosPartidos) {
      Object.entries(votosPartidos).forEach(([partidoId, votos]) => {
        if (votos && typeof votos === 'object') {
          // Buscar el nombre del partido
          const partido = partidos.find(p => p.id.toString() === partidoId);
          if (partido) {
            // Agregar diputados/senadores provinciales
            if (votos.diputados_senadores && parseInt(votos.diputados_senadores) > 0) {
              partidosTablaParaGuardar.push({
                tipo: 'LISTA',
                lista: partido.name,
                cargo: 'LEGISLADORES_PROVINCIALES',
                votos: parseInt(votos.diputados_senadores)
              });
            }

            // Agregar concejales
            if (votos.concejales && parseInt(votos.concejales) > 0) {
              partidosTablaParaGuardar.push({
                tipo: 'LISTA',
                lista: partido.name,
                cargo: 'CONCEJALES',
                votos: parseInt(votos.concejales)
              });
            }
          }
        }
      });
    }

    // Agregar todos los partidos al detalle
    // En modo editar, usar datos del PartidosTable; en modo crear, usar los arrays procesados
    if (acta?.id) {
      // Modo editar: usar datos actualizados del PartidosTable
      const detalleEditado = [];

      console.log('🔍 DEBUG - Modo editar, usando datos del PartidosTable:', partidosTableData);

      partidosTableData.forEach(item => {
        if (item && item.lista) {
          // Agregar legisladores provinciales
          if (item.legisladores_provinciales !== undefined && item.legisladores_provinciales !== null) {
            detalleEditado.push({
              tipo: 'LISTA',
              lista: item.lista,
              cargo: 'LEGISLADORES_PROVINCIALES',
              votos: parseInt(item.legisladores_provinciales) || 0
            });
          }

          // Agregar concejales
          if (item.concejales !== undefined && item.concejales !== null) {
            detalleEditado.push({
              tipo: 'LISTA',
              lista: item.lista,
              cargo: 'CONCEJALES',
              votos: parseInt(item.concejales) || 0
            });
          }
        }
      });
      datosManuales.detalle = detalleEditado;
    } else {
      // Modo crear: combinar todos los detalles procesados
      datosManuales.detalle = [
        ...datosManuales.detalle,
        ...partidosParaGuardar,
        ...partidosTablaParaGuardar
      ].filter(item => item.lista && item.lista !== 'MESA'); // Filtrar listas inválidas
    }

    // Enviar datos manuales
    onSubmit(datosManuales);
  };

  // useEffect para detectar cuando termina el envío
  useEffect(() => {
    // Si isSubmitting cambia de true a false, significa que terminó el envío
    if (!isSubmitting && acta && !acta.id) {
      // Solo limpiar si estamos en modo crear (no edición)
      // En modo crear, si no hay errores, significa que fue exitoso
      if (!errors || Object.keys(errors).length === 0) {
        console.log('✅ Envío exitoso, limpiando formulario');
        handleClearAll();
        setResetLocationSelector(true);
        if (onFormReset) {
          onFormReset();
        }
      } else {
        console.log('❌ Error en envío, manteniendo datos');
      }
    }
  }, [isSubmitting, errors, acta, onFormReset]);

  useEffect(() => {
    if (acta && acta.id) {
      // Modo edición: cargar datos del acta
      handleReset();
    } else if (acta && !acta.id) {
      // Modo crear: limpiar todos los campos
      handleClearAll();

      // IMPORTANTE: También limpiar el LocationSelector cuando se cambia a modo crear
      console.log('🔄 DEBUG - Cambiando a modo crear, limpiando LocationSelector');
      setResetLocationSelector(true);
    }
    // Si acta es null/undefined, no hacer nada (evita limpiar al cancelar)
  }, [acta]);

  return (
    <div className="card">
      <div className="card-body">
        <FormProvider {...form}>
          <form className="row" onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="col">
              <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#0d6efd' }}>
                <h5 className="text-white mb-0">
                  <i className="fas fa-map-marked-alt me-2"></i>
                  Información Electoral
                  {!!acta?.id && (
                    <small className="text-light ms-2">
                      <i className="fas fa-lock me-1"></i>
                      (Solo lectura en edición)
                    </small>
                  )}
                </h5>
              </div>

              {/* Selector de Localidad, Escuela y Mesa */}
              <LocationSelector
                isReadonly={isReadonly}
                onLocalidadChange={handleLocalidadChange}
                onEscuelaChange={handleEscuelaChange}
                onMesaChange={handleMesaChange}
                actasCargadas={actasCargadas}
                isEditing={!!acta?.id}
                initialValues={{
                  localidad: acta?.localidad || acta?.distrito || null,
                  escuela: acta?.escuela || null,
                  mesa: acta?.mesa || null
                }}
                forceReset={resetLocationSelector}
                onResetComplete={() => setResetLocationSelector(false)}
              />

              <div className="row mb-3">
                <div className="col-md-4">
                  <NumberField
                    name="electores_votaron"
                    label="A - Cantidad de electores que han votado"
                    readOnly={isReadonly}
                    autocomplete="off"
                  />
                </div>
                <div className="col-md-4">
                  <NumberField
                    name="sobres_urna"
                    label="B - Cantidad de sobres en la urna"
                    readOnly={isReadonly}
                    autocomplete="off"
                  />
                </div>
                <div className="col-md-4">
                  <NumberField
                    name="diferencia_votos"
                    label="C - Diferencia entre A y B"
                    readOnly={true}
                    autocomplete="off"
                    style={{
                      backgroundColor: '#e3f2fd',
                      fontWeight: 'bold',
                      color: '#1976d2',
                      border: '2px solid #2196f3',
                      borderRadius: '8px'
                    }}
                  />
                  <small className="form-text text-info">
                    <i className="fas fa-calculator me-1"></i>
                    Calculado automáticamente (A - B)
                  </small>
                </div>
              </div>

              <hr className="my-4" />

              {/* Nueva tabla de partidos estilo certificado */}
              {console.log('🔍 ActaForm - acta:', acta, 'acta?.id:', acta?.id, 'acta?.detalle:', acta?.detalle)}
              {console.log('🔍 DEBUG - PartidosTable recibirá:', {
                isEditing: !!acta?.id,
                initialData: acta?.detalle || [],
                initialDataLength: (acta?.detalle || []).length
              })}
              <PartidosTable
                key={partidosTableKey}
                form={form}
                isReadonly={isReadonly}
                onPartidosChange={handlePartidosChange}
                onVotosChange={handlePartidosVotosChange}
                onDataChange={handlePartidosTableDataChange}
                initialData={acta?.detalle || []}
                isEditing={!!acta?.id}
              />

              {/* Votos especiales integrados */}
              <VotosEspeciales
                form={form}
                isReadonly={isReadonly}
                onVotoChange={handleEspecialesChange}
                initialData={acta?.especiales || {}}
                isEditing={!!acta?.id}
                resetTrigger={partidosTableKey}
              />




              <hr className="my-4" />
              <div className="bg-light p-4 rounded border">
                <h5 className="mb-3 text-center" style={{ color: '#6f42c1' }}>
                  <i className="fas fa-calculator me-2"></i>
                  Totales
                </h5>
                <div className="row justify-content-center">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label fw-bold text-muted">Total Diputados o Senadores Provinciales</label>
                      <input
                        type="text"
                        className="form-control form-control-lg text-center fw-bold"
                        value={totalLegisladores}
                        readOnly
                        style={{
                          fontSize: '1.5rem',
                          backgroundColor: '#ffffff',
                          border: '3px solid #6f42c1',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          color: '#6f42c1'
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label fw-bold text-muted">Total Concejales y Consejeros Escolares</label>
                      <input
                        type="text"
                        className="form-control form-control-lg text-center fw-bold"
                        value={totalConcejales}
                        readOnly
                        style={{
                          fontSize: '1.5rem',
                          backgroundColor: '#ffffff',
                          border: '3px solid #6f42c1',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          color: '#6f42c1'
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="row justify-content-center">
                  <div className="col-md-8">

                  </div>
                </div>

                {/* Mensaje de aviso si no coinciden los totales */}
                
              </div>

              {/* Archivos Adjuntos */}
              <div className="card mb-4">
                <div className="card-header text-white" style={{ backgroundColor: '#fd7e14' }}>
                  <h5 className="mb-0">
                    <i className="fas fa-paperclip me-2"></i>
                    Archivos Adjuntos
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div style={{ maxWidth: '200px', margin: '0 auto' }}>
                        <ImageField label="Foto" name="foto" readOnly={isReadonly} />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div style={{ maxWidth: '200px', margin: '0 auto' }}>
                        <ImageField label="Foto segunda página" name="foto2" readOnly={isReadonly} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subir imagen antes del botón de enviar */}


              {!isReadonly && (
                <div className="d-flex justify-content-center mt-4">
                  {!isSubmitting ? <button
                    className="btn btn-lg"
                    type="submit"
                    
                    style={{ backgroundColor: '#e2d9f3', borderColor: '#e2d9f3', color: '#6f42c1' }}
                  >
                    Enviar Acta
                  </button> : <Spinner/>}
                </div>
              )}


            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

ActaForm.propTypes = {
  onSubmit: PropTypes.func,
  acta: PropTypes.object,
  isReadonly: PropTypes.bool,
  isSubmitting: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  actasCargadas: PropTypes.array,
  onFormReset: PropTypes.func
};

ActaForm.defaultProps = {
  onSubmit: () => { },
  acta: {},
  isReadonly: false,
  onCancel: () => { },
  actasCargadas: [],
  onFormReset: () => { }
};








export default ActaForm;


